import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Building2, KeyRound } from "lucide-react";
import { getSupabaseClient, useAuthStore } from "@veorest/api-client";
import {
  AUTH_ICONS,
  AuthError,
  AuthField,
  AuthShell,
  AuthSubmit,
} from "../components/auth/AuthShell";
import {
  authErrorKey,
  mustChangePassword,
  resolvePartnerSession,
  toLoginEmail,
  usePartnerSessionStore,
} from "../lib/partnerAuth";
import {
  completePartnerOnboarding,
  peekPartnerPasscode,
  PartnerSignupError,
  type PartnerPeek,
  type PartnerSignupErrorCode,
} from "../lib/partnerSignup";
import { useI18n, type TranslationKey } from "../i18n";
import { ROUTES } from "../routes";

type AuthMode = "login" | "passcode" | "onboarding";

const SIGNUP_ERROR_KEY: Record<PartnerSignupErrorCode, TranslationKey> = {
  passcode_invalid: "auth.passcodeInvalid",
  passcode_expired: "auth.passcodeExpired",
  passcode_used: "auth.passcodeUsed",
  passcode_attempts: "auth.passcodeAttempts",
  username_invalid: "auth.usernameInvalid",
  username_taken: "auth.usernameTaken",
  email_invalid: "auth.emailInvalid",
  email_taken: "auth.emailTaken",
  password_too_short: "auth.passwordTooShort",
  auth_failed: "auth.authFailed",
  onboarding_failed: "auth.onboardingFailed",
};

function signupErrorKey(error: unknown): TranslationKey {
  if (error instanceof PartnerSignupError) return SIGNUP_ERROR_KEY[error.code];
  return "auth.onboardingFailed";
}

export default function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setUser, setSession } = useAuthStore();
  const { setSession: setPartnerSession, clear } = usePartnerSessionStore();
  const [mode, setMode] = useState<AuthMode>("login");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sign-in (existing partner)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Passcode (new partner)
  const [passcode, setPasscode] = useState("");
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  // Onboarding (after a valid passcode)
  const [partnerPeek, setPartnerPeek] = useState<PartnerPeek | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");

  const switchMode = (next: AuthMode) => {
    setError("");
    setMode(next);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const supabase = getSupabaseClient();
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: toLoginEmail(email),
        password,
      });

      if (authError) {
        const key = authErrorKey(authError.message);
        const message = t(key as TranslationKey);
        setError(message);
        toast.error(message);
        return;
      }

      const user = data.user;
      if (!user) {
        await supabase.auth.signOut();
        setError(t("auth.authFailed"));
        return;
      }

      setSession(data.session);
      setUser(user);

      if (mustChangePassword(user)) {
        clear();
        navigate(ROUTES.CHANGE_PASSWORD);
        return;
      }

      const partnerSession = await resolvePartnerSession();
      if (!partnerSession) {
        clear();
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        setError(t("auth.notPartner"));
        return;
      }

      setPartnerSession(partnerSession);
      toast.success(t("auth.signedIn"));
      navigate(ROUTES.DASHBOARD);
    } catch {
      setError(t("auth.signInFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasscodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const code = passcode.trim();
    if (code.length < 4) {
      const message = t("auth.passcodeInvalid");
      setError(message);
      toast.error(message);
      return;
    }
    setIsCheckingCode(true);
    try {
      const result = await peekPartnerPasscode(code);
      setPartnerPeek(result);
      setDisplayName(result.name ?? "");
      setContactEmail(result.email ?? "");
      switchMode("onboarding");
    } catch (err) {
      const message = t(signupErrorKey(err));
      setError(message);
      toast.error(message);
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleOnboardingSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      await completePartnerOnboarding({
        passcode,
        displayName,
        username,
        email: contactEmail,
        password: accountPassword,
      });

      const supabase = getSupabaseClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);

      if (mustChangePassword(session?.user ?? null)) {
        clear();
        navigate(ROUTES.CHANGE_PASSWORD);
        return;
      }

      const partnerSession = await resolvePartnerSession();
      if (partnerSession) {
        setPartnerSession(partnerSession);
        toast.success(t("auth.partnerActivated"));
        navigate(ROUTES.DASHBOARD);
        return;
      }

      // Activation finished but the membership lookup failed — fall back to
      // a normal sign-in instead of bouncing through the protected gate.
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      toast.success(t("auth.setupComplete"));
      switchMode("login");
      setEmail(username);
      setPassword("");
    } catch (err) {
      const message = t(signupErrorKey(err));
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copy: Record<AuthMode, { title: string; subtitle: string }> = {
    login: { title: t("auth.loginTitle"), subtitle: t("auth.loginSubtitle") },
    passcode: {
      title: t("auth.passcodeTitle"),
      subtitle: t("auth.passcodeSubtitle"),
    },
    onboarding: {
      title: t("auth.onboardingTitle"),
      subtitle: t("auth.onboardingSubtitle"),
    },
  };

  return (
    <AuthShell title={copy[mode].title} subtitle={copy[mode].subtitle}>
      {error ? <AuthError message={error} /> : null}

      {mode === "login" ? (
        <>
          <form onSubmit={handleSubmit} className="auth-form">
            <AuthField
              id="email"
              label={t("auth.loginId")}
              type="text"
              value={email}
              onChange={setEmail}
              placeholder={t("auth.emailPlaceholder")}
              icon={AUTH_ICONS.mail}
              autoComplete="username"
              required
              autoFocus
            />
            <AuthField
              id="password"
              label={t("auth.password")}
              type="password"
              value={password}
              onChange={setPassword}
              placeholder={t("auth.passwordPlaceholder")}
              icon={AUTH_ICONS.lock}
              autoComplete="current-password"
              required
            />
            <AuthSubmit
              loading={isSubmitting}
              loadingLabel={t("auth.signingIn")}
              label={t("auth.signIn")}
            />
          </form>
          <p className="auth-link-row">
            <button
              type="button"
              className="auth-link"
              onClick={() => switchMode("passcode")}
            >
              <KeyRound className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
              {t("auth.newPartnerLink")}
            </button>
          </p>
        </>
      ) : null}

      {mode === "passcode" ? (
        <>
          <form onSubmit={handlePasscodeSubmit} className="auth-form">
            <label className="auth-field auth-field--code" htmlFor="passcode">
              <span className="auth-field__label">{t("auth.passcodeLabel")}</span>
              <span className="auth-field__control">
                <KeyRound
                  className="auth-field__icon"
                  strokeWidth={2.25}
                  aria-hidden
                />
                <input
                  id="passcode"
                  type="text"
                  className="auth-field__input auth-field__input--code"
                  value={passcode}
                  onChange={(e) =>
                    setPasscode(
                      e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 8)
                    )
                  }
                  placeholder={t("auth.passcodePlaceholder")}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </span>
            </label>
            <AuthSubmit
              loading={isCheckingCode}
              loadingLabel={t("auth.checkingCode")}
              label={t("auth.continue")}
            />
          </form>
          <p className="auth-link-row">
            <button
              type="button"
              className="auth-link auth-link--muted"
              onClick={() => switchMode("login")}
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
              {t("auth.backToSignIn")}
            </button>
          </p>
        </>
      ) : null}

      {mode === "onboarding" ? (
        <>
          <form onSubmit={handleOnboardingSubmit} className="auth-form">
            {partnerPeek?.name ? (
              <p className="auth-partner-chip">
                <Building2 className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                <span>{partnerPeek.name}</span>
              </p>
            ) : null}
            <AuthField
              id="displayName"
              label={t("auth.displayName")}
              value={displayName}
              onChange={setDisplayName}
              icon={AUTH_ICONS.user}
              autoComplete="name"
              required
              autoFocus
            />
            <AuthField
              id="username"
              label={t("auth.username")}
              value={username}
              onChange={setUsername}
              icon={AUTH_ICONS.atSign}
              autoComplete="username"
              required
            />
            <p className="auth-field-hint">{t("auth.usernameHint")}</p>
            <AuthField
              id="contact-email"
              label={t("auth.emailOptional")}
              type="email"
              value={contactEmail}
              onChange={setContactEmail}
              placeholder={t("auth.emailHint")}
              icon={AUTH_ICONS.mail}
              autoComplete="email"
            />
            <AuthField
              id="account-password"
              label={t("auth.password")}
              type="password"
              value={accountPassword}
              onChange={setAccountPassword}
              minLength={8}
              icon={AUTH_ICONS.lock}
              autoComplete="new-password"
              required
            />
            <AuthSubmit
              loading={isSubmitting}
              loadingLabel={t("auth.activatingAccount")}
              label={t("auth.activateAccount")}
            />
          </form>
          <p className="auth-link-row">
            <button
              type="button"
              className="auth-link auth-link--muted"
              onClick={() => switchMode("passcode")}
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
              {t("auth.back")}
            </button>
          </p>
        </>
      ) : null}
    </AuthShell>
  );
}
